import React from "react";
import styled, { css } from "styled-components";
import { fonts, colors } from "./mixins.js";
import { BREAKPOINT_RESERVATION_PAGE } from "../../pageComponents/reservationPage/constants/breakpoints";

interface StyledIconProps {
  width?: string;
  height?: string;
  $icon?: string;
}

interface StyledButtonProps {
  $variant?: "primary" | "secondary" | "banner" | "tertiary";
  width?: string;
  height?: string;
  $fullWidth?: boolean;
  $hasIconComponent?: boolean;
  $md?: boolean;
  disabled?: boolean;
}

interface StyledTextProps {
  width?: string;
  disabled?: boolean;
  $variant?: "primary" | "secondary" | "banner" | "tertiary";
}

interface ButtonProps {
  variant?: "primary" | "secondary" | "banner" | "tertiary";
  type?: "button" | "submit" | "reset";
  text: string;
  icon?: string;
  iconBefore?: React.ReactNode;
  iconComponent?: React.ReactNode;
  url?: string;
  width?: string;
  height?: string;
  fullWidth?: boolean;
  md?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  target?: string;
  id?: string;
  className?: string;
}

const StyledIcon = styled.i<StyledIconProps>`
  display: flex;
  align-items: center;
  width: ${(props) => props.width || "24px"};
  height: ${(props) => props.height || "24px"};
  background-size: contain;
  background-repeat: no-repeat;
  ${(props) =>
    props.$icon === "print" &&
    css`
      background-image: url("/Site/images/icons/order-details/print-white.svg");
    `}
`;

const StyledButton = styled.button<StyledButtonProps>`
  display: flex;
  gap: 8px;
  width: ${(props) =>
    props.$fullWidth ? "100%" : props.width || "fit-content"};
  height: ${(props) => props.height || "fit-content"};
  align-items: center;
  border: none;
  -webkit-appearance: none;
  appearance: none;
  transition:
    background-color 0.3s ease,
    color 0.3s ease,
    border-color 0.3s ease;

  @media print {
    display: none !important;
  }

  // Explicit keyboard focus ring: legacy site stylesheets can strip the
  // browser default. Mouse focus is still suppressed per variant below.
  &:focus-visible {
    outline: 1px solid ${colors.primaryButton};
    outline-offset: 1px;
  }

  // Rendered as <a> when given a url: beat the site-wide a:hover underline.
  // Kept outside the variants' @media (hover: hover) blocks because touch
  // devices also apply :hover on tap.
  &:hover {
    text-decoration: none;
  }

  ${(props) =>
    props.$variant === "primary" &&
    css<StyledButtonProps>`
      display: flex;
      padding: ${(props) =>
        props.$hasIconComponent
          ? "16px 20px 16px 24px"
          : props.$fullWidth || props.$md
            ? "16px 24px"
            : "8px 16px"};
      justify-content: center;
      align-items: center;
      color: ${(props) => (props.disabled ? "#A8ABAF" : colors.buttonText)};
      background-color: ${(props) =>
        props.disabled ? "#E9E8EC" : colors.primaryButton};
      cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
      text-decoration: none;
      border-radius: 24px;

      @media (hover: hover) {
        &:not(:disabled):hover {
          background-color: ${colors.buttonHovered};
          color: ${colors.buttonTextHovered};
          text-decoration: none;
          div {
            color: ${colors.buttonTextHovered};
          }
        }
      }

      &:active:not(:disabled) {
        background-color: ${colors.buttonPressed};
      }

      &:focus:not(:focus-visible) {
        outline: none;
      }

      &:disabled {
        background-color: #e9e8ec;
        color: #a8abaf;
        cursor: not-allowed;
        pointer-events: none;
      }
    `}

  ${(props) =>
    props.$variant === "banner" &&
    css<StyledButtonProps>`
      display: flex;
      padding: 8px 24px;
      justify-content: center;
      align-items: center;
      color: ${(props) => (props.disabled ? "#A8ABAF" : colors.buttonText)};
      background-color: ${(props) =>
        props.disabled ? "#E9E8EC" : colors.primaryButton};
      cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
      text-decoration: none;
      border-radius: 24px;

      @media (hover: hover) {
        &:not(:disabled):hover {
          background-color: ${colors.buttonHovered};
          text-decoration: none;
        }
      }

      &:active:not(:disabled) {
        background-color: ${colors.buttonPressed};
      }

      &:focus:not(:focus-visible) {
        outline: none;
      }

      &:disabled {
        background-color: #e9e8ec;
        color: #a8abaf;
        cursor: not-allowed;
        pointer-events: none;
      }
    `}

  ${(props) =>
    props.$variant === "tertiary" &&
    css`
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font-family: "pf_highway_sans_pro_lightRg";
      font-size: 14px;
      font-weight: 600;
      color: ${colors.secondaryText};
      text-decoration: none;
      display: flex;
      gap: 8px;
      align-items: center;

      svg {
        stroke: currentColor;
      }

      @media (hover: hover) {
        &:not(:disabled):hover {
          div {
            color: ${colors.secondaryTextHovered};
          }
        }
      }

      &:active:not(:disabled) {
        div {
          color: ${colors.secondaryTextPressed};
        }
      }

      &:focus:not(:focus-visible) {
        outline: none;
      }

      &:disabled {
        color: #a8abaf;
        cursor: not-allowed;
        pointer-events: none;
        div {
          color: #a8abaf;
        }
      }
    `}

  ${(props) =>
    props.$variant === "secondary" &&
    css<StyledButtonProps>`
      display: flex;
      padding: ${(props) =>
        props.$hasIconComponent
          ? "16px 20px 16px 24px"
          : props.$fullWidth || props.$md
            ? "16px 24px"
            : "8px 16px"};
      justify-content: center;
      align-items: center;
      color: ${(props) => (props.disabled ? "#A8ABAF" : colors.primary)};
      background-color: ${(props) => (props.disabled ? "#F8F9FA" : "#FFFFFF")};
      border: 1px solid
        ${(props) => (props.disabled ? "#E9E8EC" : colors.buttonsBorder)};
      cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
      text-decoration: none;
      border-radius: 24px;

      @media (hover: hover) {
        &:not(:disabled):hover {
          border: 1px solid ${colors.buttonHovered};
          color: ${colors.secondaryTextHovered};
          text-decoration: none;
          div {
            color: ${colors.secondaryTextHovered};
          }
        }
      }

      &:active:not(:disabled) {
        color: ${colors.secondaryTextPressed};
        text-decoration: none;
        div {
          color: ${colors.secondaryTextPressed};
        }
      }

      &:focus:not(:focus-visible) {
        outline: none;
      }

      &:disabled {
        background-color: #fff;
        color: #9897a9;
        border: 1px solid #e9e8ec;
        cursor: not-allowed;
        pointer-events: none;
      }
    `}
`;

const StyledText = styled.div<StyledTextProps>`
  ${(props) =>
    props.width === "100%"
      ? fonts.normalTextBold()
      : fonts.normalTextSmallBold()}
  color: ${(props) => {
    if (props.disabled) return "#A8ABAF";
    if (props.$variant === "primary" || props.$variant === "banner")
      return colors.buttonText;
    return colors.secondaryText;
  }};

  ${(props) =>
    props.$variant === "tertiary" &&
    css<StyledTextProps>`
      font-family: inherit;
      font-size: 14px;
      font-weight: inherit;
      line-height: inherit;
      color: inherit;

      @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
        font-size: 14px;
      }
    `}
  line-height: ${(props) => (props.width === "100%" ? "16px" : "14px")};
  transition: color 0.2s ease;

  ${(props) =>
    props.$variant === "banner" &&
    css`
      font-size: 16px;
      line-height: 16px;
      font-weight: 400;
    `}
`;

const Button: React.FC<ButtonProps> = ({
  variant,
  type,
  text,
  icon,
  iconBefore,
  iconComponent,
  url,
  width,
  height,
  fullWidth,
  md,
  onClick,
  disabled = false,
  target = "_blank",
  id,
  className,
}) => {
  return (
    <StyledButton
      $variant={variant}
      type={url ? undefined : type} // If it's a link, we don't want to set the button type. Its default behavior will be to navigate to the URL.
      target={target}
      href={url}
      width={width}
      height={height}
      $fullWidth={fullWidth}
      $md={md}
      $hasIconComponent={!!iconComponent}
      onClick={onClick}
      as={url ? "a" : "button"}
      id={id}
      disabled={disabled}
      className={className}
    >
      {icon && <StyledIcon $icon={icon} />}
      {iconBefore}
      <StyledText width={width} disabled={disabled} $variant={variant}>
        {text}
      </StyledText>
      {iconComponent}
    </StyledButton>
  );
};

export default Button;
